import React, { useMemo } from 'react';
import { EngineReferenceStudioIcon } from '@modules/ui/icons/EngineReferenceStudioIcon';
import { Divider, makeStyles, Tabs, Typography } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { ReferenceType } from '@modules/client/types/globalTypes';
import {
  labelTranslKeyByReferenceType,
  referenceTypePathMapping,
  referenceTypeToReferencesKey,
} from '@modules/apiReference/page/constants/apiReferenceConstants';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import {
  getReferences,
  getGlobalMemberToFileMap,
} from '../page/sharedUI/utils/linkableTypeReferenceUtils';

const useLandingSectionStyles = makeStyles()((theme) => ({
  introductionHeadline: {
    fontWeight: 'bold',
  },
  introductionDescription: {
    marginTop: '16px',
    marginBottom: '32px',
  },
  description: {
    marginBottom: '12px',
    marginTop: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '12px 12px 8px 12px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
    margin: '12px',
    marginBottom: '24px',
    marginTop: '24px',
    [theme.breakpoints.down('Medium')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  sentenceCaseTab: {
    '&.MuiTab-root': {
      textTransform: 'none', // translations are already in sentence case
    },
  },
}));

interface EngineReferenceLandingTabsProps {
  referenceType: ReferenceType;
  showIntroduction?: boolean;
}

// Define all available reference types for tabs
const availableReferenceTypes = [
  ReferenceType.CLASS,
  ReferenceType.DATATYPE,
  ReferenceType.ENUM,
  ReferenceType.GLOBAL,
  ReferenceType.LIBRARY,
];

const referenceTypeToDescriptionTranslKey: Record<ReferenceType, string> = {
  [ReferenceType.CLASS]: 'Description.EngineClasses',
  [ReferenceType.DATATYPE]: 'Description.EngineDataTypes',
  [ReferenceType.ENUM]: 'Description.EngineEnums',
  [ReferenceType.GLOBAL]: 'Description.EngineGlobals',
  [ReferenceType.LIBRARY]: 'Description.EngineLibraries',
};

// Component to render reference list content
const ReferenceListContent: React.FC<{
  referenceType: ReferenceType;
  referencesList: string[];
}> = ({ referenceType, referencesList }) => {
  const { translate } = useTranslation();
  const { classes: styles } = useLandingSectionStyles();

  const memberToFile = useMemo(() => {
    if (referenceType !== ReferenceType.GLOBAL) return {};
    const globalByFile = getReferences().GlobalByFile;
    return globalByFile ? getGlobalMemberToFileMap(globalByFile) : {};
  }, [referenceType]);

  // For globals, libraries, and data types, just list them in order
  const shouldUseSimpleList =
    referenceType === ReferenceType.GLOBAL ||
    referenceType === ReferenceType.LIBRARY ||
    referenceType === ReferenceType.DATATYPE;

  if (shouldUseSimpleList) {
    // Sort case-insensitively, with items starting with underscore at the end
    const sortedReferences = [...referencesList].sort((a, b) => {
      const aStartsWithUnderscore = a.startsWith('_');
      const bStartsWithUnderscore = b.startsWith('_');

      // If one starts with underscore and the other doesn't, underscore goes to the end
      if (aStartsWithUnderscore && !bStartsWithUnderscore) return 1;
      if (!aStartsWithUnderscore && bStartsWithUnderscore) return -1;

      // Otherwise, sort case-insensitively
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });

    return (
      <div className={styles.grid}>
        {sortedReferences.map((referenceName) => (
          <EngineReferenceStudioIcon
            key={referenceName}
            name={referenceName}
            referenceType={referenceType}
            showName
            globalFileName={
              referenceType === ReferenceType.GLOBAL ? memberToFile[referenceName] : undefined
            }
          />
        ))}
      </div>
    );
  }

  // Group references by first letter for classes and enums
  const groupedByLetter = referencesList.reduce(
    (acc, referenceName) => {
      if (!referenceName || referenceName.length === 0) return acc;
      const letter = referenceName[0].toUpperCase();
      if (!acc[letter]) {
        acc[letter] = [];
      }
      acc[letter].push(referenceName);
      return acc;
    },
    {} as Record<string, string[]>,
  );

  // Sort letters alphabetically
  const sortedLetters = Object.keys(groupedByLetter).sort();

  return (
    <React.Fragment>
      {sortedLetters.map((letter) => {
        const letterReferences = groupedByLetter[letter];

        return (
          <div key={letter}>
            <div className={styles.header}>
              <Typography variant='h2'>{letter}</Typography>
              <Typography variant='body1' color='disabled'>
                {letterReferences.length} {translate(labelTranslKeyByReferenceType[referenceType])}
              </Typography>
            </div>
            <Divider />
            <div className={styles.grid}>
              {letterReferences.map((referenceName) => (
                <EngineReferenceStudioIcon
                  key={referenceName}
                  name={referenceName}
                  referenceType={referenceType}
                  showName
                />
              ))}
            </div>
          </div>
        );
      })}
    </React.Fragment>
  );
};

const EngineReferenceLandingTabs: React.FC<EngineReferenceLandingTabsProps> = ({
  referenceType,
  showIntroduction = false,
}) => {
  const { translate } = useTranslation();
  const references = getReferences();
  const { treeListData } = useSideBarContent();
  const { classes: styles } = useLandingSectionStyles();

  const tabsData = useMemo(
    () =>
      availableReferenceTypes.map((type) => {
        const referencesList = references?.[referenceTypeToReferencesKey[type]] || [];
        return {
          type,
          label: `${translate(labelTranslKeyByReferenceType[type])} (${referencesList.length})`,
          referencesList,
        };
      }),
    [references, translate],
  );

  // Get the first release note from navigation tree
  const latestReleaseNotePath = useMemo(() => {
    // Find section containing release notes by checking node paths
    const releaseNotesSection = treeListData.find((section) =>
      section.nodes.some((node) => node.path?.startsWith('/release-notes/')),
    );
    // Get the first node which should be the latest
    const firstNode = releaseNotesSection?.nodes[0];
    return firstNode?.path ?? null;
  }, [treeListData]);

  const activeTabData = tabsData.find((tab) => tab.type === referenceType);

  return (
    <React.Fragment>
      {showIntroduction && (
        <React.Fragment>
          <Typography variant='body1' color='secondary' className={styles.introductionHeadline}>
            {translate('Description.BuildLogicIntoYourExperiences') ||
              'Build logic into your experiences with the Roblox Engine APIs.'}
          </Typography>
          <Typography variant='body1' color='secondary' className={styles.introductionDescription}>
            {translate('Description.EngineReferenceIntroduction') ||
              'This API reference documents all of the classes, data types, enumerations, functions, events, callbacks, and properties that you can use when creating on Roblox.'}
          </Typography>
        </React.Fragment>
      )}
      <Tabs value={referenceType} variant='scrollable'>
        {tabsData.map(({ type, label }) => (
          <DynamicallyRouted.Tab
            key={type}
            tabIndex={0}
            role='link'
            href={`/reference/engine/${referenceTypePathMapping[type]}`}
            label={label}
            value={type}
            className={styles.sentenceCaseTab}
          />
        ))}
        {latestReleaseNotePath && (
          <DynamicallyRouted.Tab
            key='release-notes'
            tabIndex={0}
            role='link'
            href={latestReleaseNotePath}
            label={translate('Heading.ReleaseNotes') || 'Release notes'}
            value='release-notes'
            className={styles.sentenceCaseTab}
          />
        )}
      </Tabs>
      {activeTabData && (
        <React.Fragment>
          <Typography variant='body1' color='secondary' className={styles.description}>
            {translate(referenceTypeToDescriptionTranslKey[referenceType])}
          </Typography>
          <ReferenceListContent
            referenceType={activeTabData.type}
            referencesList={activeTabData.referencesList}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default EngineReferenceLandingTabs;
